import { invariant } from "@pexip-engage/utils";
import { PluginState } from "@pexip-engage-public/plugin-state";
import type { ActionFunctionArgs, LoaderFunctionArgs } from "@remix-run/node";
import { json, redirect } from "@remix-run/node";
import { useLoaderData, useRouteLoaderData } from "@remix-run/react";
import { z } from "zod";

import { AppErrorBoundary } from "~/components/AppErrorBoundary";
import { createServerStateManager } from "~/modules/StateManager.server";
import { Timetable } from "~/modules/timetable/components/Timetable";
import {
  TimetableContextProvider,
  useTimetableRouteContext,
} from "~/modules/timetable/components/Timetable.hooks";
import { handleTimetableAction } from "~/modules/timetable/timetable.action.server";
import { handleTimetableLoader } from "~/modules/timetable/timetable.loader.server";
import { timetableErrors } from "~/routes/$enterprise+/plugin+/$sessionId.schedule_.confirmation";
import { updateAppointmentPossibility } from "~/services/appointment.api.server";
import { HttpError } from "~/services/utils.server";
import { getAppointmentData } from "~/utils/appointment.utils.server";
import { convertISOStringToDate } from "~/utils/date";
import { LoggerMeta } from "~/utils/logger.server";
import { setTimetableOverlappingSlotError } from "~/utils/timetable.utils";
import { errors } from "~/utils/validators.utils";
import { zDate } from "~/utils/zod.utils";

export async function action(args: ActionFunctionArgs) {
  return args.context.measurer.time(
    "$enterprise_schedule_timetable_action",
    async function scheduleTimetableLoader() {
      const { stateManager } = await handleTimetableAction(await createServerStateManager(args));
      stateManager.dispatchCAEvent({ type: "appointment-requested", payload: {} });

      const { appointmentId, possibilityId, timeSlot } = validateFormData(stateManager.state);

      const updatedAppointment = await updateAppointmentPossibility({
        appointmentId,
        timeSlot,
        config: await stateManager.getAuthorizedRequestConfig(),
        possibilityId,
      }).catch(async (error: unknown) => {
        if (error instanceof Response) {
          const body = await error.clone().json();

          const err = "error" in body && (body.error as HttpError);
          if (err && err?.parsedError?.status === 409) {
            const hasTimetableError = err.parsedError.errors.some((error) =>
              timetableErrors.includes(error.code),
            );

            if (hasTimetableError) {
              return;
            }
          }
        }

        // All other errors should be handled by the catch boundary.
        throw error;
      });

      const appointment = await getAppointmentData(stateManager);

      if (!updatedAppointment) {
        await setTimetableOverlappingSlotError(stateManager).persist();

        const searchParams = new URLSearchParams();
        const startDate = appointment.possibility
          ? convertISOStringToDate(appointment.possibility.start)
          : undefined;

        startDate && searchParams.set("startDate", startDate);

        return redirect(`../timetable?${searchParams}`);
      }

      stateManager.dispatchCAEvent({
        type: "appointment-confirmed",
        payload: { appointmentId: Number.parseInt(appointmentId) },
      });

      await stateManager.updateWithAppointment(appointment).persist();

      return redirect("../confirmation");
    },
  );
}

export function useEditTimetableLoaderData() {
  return useRouteLoaderData<typeof loader>(
    "routes/$enterprise+/plugin+/$sessionId.appointment.edit.timetable",
  );
}

export async function loader(args: LoaderFunctionArgs) {
  return args.context.measurer.time(
    "$enterprise/edit/timetable",
    async function editTimetableLoader() {
      const stateManager = await createServerStateManager(args);
      const result = await handleTimetableLoader(stateManager);

      if (result.type === "redirect") {
        // This should not happen here.
        args.context.logger.error<LoggerMeta>(
          {
            extra: { state: stateManager.toString() },
          },
          "ERROR Failed to resolve recaptcha or next step in resourceCode/timetable",
        );
        invariant(false, "Unexpected route hit in $enterprise/edit/timetable");
      }

      return json(result.payload);
    },
  );
}

export default function EditTimetableRoute() {
  const props = useLoaderData<typeof loader>();
  const context = useTimetableRouteContext();

  return (
    <TimetableContextProvider value={context}>
      <Timetable {...props} />
    </TimetableContextProvider>
  );
}

export function ErrorBoundary() {
  return <AppErrorBoundary />;
}

const timetableValidationSchema = z.object({
  appointmentId: z.string(),
  possibilityId: z.string(),
  timeSlot: z.object({
    start: zDate,
    end: zDate,
    timeZone: z.string(),
  }),
});

function validateFormData(state: PluginState) {
  const payload = {
    appointmentId: state.appointmentId,
    possibilityId: state.possibilityId,
    timeSlot: state.timetable,
  };

  const validation = timetableValidationSchema.safeParse(payload);

  if (!validation.success) {
    throw json(
      ...errors.validation.create({
        translation: { key: "errors.generic" },
        source: "edit.timetable-validateFormData",
        errors: validation.error,
        extra: payload,
      }),
    );
  }

  return validation.data;
}
